import { type SectionProps } from '@/components/features/Editor/utils';
import { Image } from '@/components/shared/Image';
import { useRefScrollProgress } from '@/hooks/useRefScrollProgress';
import { AspectRatio, Box, Container, Flex, Heading, Text } from '@chakra-ui/react';
import { MotionBox } from '@sphere/ui';
import { useScroll, useTransform } from 'framer-motion';
import { rem } from 'polished';
import { useRef, useState, type RefObject } from 'react';
import { useEffectOnce } from 'react-use';
import { ActiveItemWrapper } from '../../Editor/components/ActiveItemWrapper';

interface Props extends SectionProps {
  containerRef?: RefObject<HTMLElement>;
  isPreview?: boolean;
  activeItem?:
    | 'firstImage-image'
    | 'firstImage-caption'
    | 'secondImage-image'
    | 'secondImage-caption'
    | 'thirdImage-image'
    | 'thirdImage-caption'
    | 'title';
}

export const ImageCollectionBlock = ({
  containerRef,
  images,
  title,
  isPreview,
  activeItem,
}: Props) => {
  const [offsets, setOffsets] = useState({ first: 0, second: 0, third: 0 });

  const scrollProgressContainer = useRef(null);
  const [start, end] = useRefScrollProgress(scrollProgressContainer);
  const { scrollYProgress: viewPortScrollYProgress } = useScroll(
    containerRef ? { container: containerRef, target: scrollProgressContainer } : {},
  );
  const scrollProgress = useTransform(viewPortScrollYProgress, [start || 0, end || 0], [0, 1]);

  const transformations = {
    first: useTransform(containerRef ? viewPortScrollYProgress : scrollProgress, [0, 1], [0, -50]),
    second: useTransform(
      containerRef ? viewPortScrollYProgress : scrollProgress,
      [0, 1],
      [0, -200],
    ),
    third: useTransform(containerRef ? viewPortScrollYProgress : scrollProgress, [0, 1], [0, -300]),
  };

  useEffectOnce(() => {
    Object.entries(transformations).map(([key, value]) =>
      value.onChange(value =>
        setOffsets(prevState => ({ ...prevState, [key]: Math.round(value) })),
      ),
    );
  });

  return (
    <Container variant="breakout" bg="black">
      <Flex
        display={{ base: 'none', md: 'flex' }}
        pt={{ base: 'space.40', md: 'space.96' }}
        pb={isPreview ? { base: 'space.40', md: 'space.96' } : { md: 'space.48' }}
        pl="space.64"
        ref={scrollProgressContainer}
        pos="relative"
        color="white"
      >
        <Box w="50%" pt={{ base: 'space.132', lg: rem(396) }}>
          {images?.[0] && (
            <MotionBox
              animate={
                isPreview ? undefined : { translateY: offsets.first, transition: { duration: 0 } }
              }
            >
              <Flex flexDir="column">
                <ActiveItemWrapper
                  isActive={activeItem === 'firstImage-image'}
                  isPreview={isPreview}
                >
                  <AspectRatio ratio={9 / 12}>
                    <Image
                      fill
                      src={images?.[0].src}
                      alt=""
                      objectFit="cover"
                      pointerEvents={isPreview ? 'none' : undefined}
                    />
                  </AspectRatio>
                </ActiveItemWrapper>
                {images?.[0].caption && (
                  <Text
                    mt="space.16"
                    bg={activeItem === 'firstImage-caption' ? 'arcticBlue' : 'unset'}
                    transition="all 120ms ease-in-out"
                  >
                    {images?.[0].caption}
                  </Text>
                )}
                {title && (
                  <Heading
                    fontSize={{ base: '3xl', lg: '4xl' }}
                    ml={{ base: 'space.80', lg: 'space.120' }}
                    mt={{ base: 'space.64', lg: 'space.80' }}
                    maxW={rem(480)}
                    bg={activeItem === 'title' ? 'arcticBlue' : 'unset'}
                    transition="all 120ms ease-in-out"
                    letterSpacing="subtitle"
                  >
                    {title}
                  </Heading>
                )}
              </Flex>
            </MotionBox>
          )}
        </Box>
        <Box w="50%">
          {images?.[1] && (
            <MotionBox
              animate={
                isPreview ? undefined : { translateY: offsets.second, transition: { duration: 0 } }
              }
              zIndex={2}
            >
              <Flex w="60%" flexDir="column" transform="translateX(-60px)">
                <ActiveItemWrapper
                  isActive={activeItem === 'secondImage-image'}
                  isPreview={isPreview}
                >
                  <AspectRatio ratio={7 / 10}>
                    <Image
                      fill
                      src={images?.[1].src}
                      alt=""
                      objectFit="cover"
                      pointerEvents={isPreview ? 'none' : undefined}
                    />
                  </AspectRatio>
                </ActiveItemWrapper>
                {images?.[1].caption && (
                  <Text
                    mt="space.16"
                    textAlign="right"
                    bg={activeItem === 'secondImage-caption' ? 'arcticBlue' : 'unset'}
                    transition="all 120ms ease-in-out"
                  >
                    {images?.[1].caption}
                  </Text>
                )}
              </Flex>
            </MotionBox>
          )}
          {images?.[2] && (
            <MotionBox
              animate={
                isPreview ? undefined : { translateY: offsets.third, transition: { duration: 0 } }
              }
            >
              <Flex mt={rem(200)} ml="auto" w="75%" flexDir="column">
                <ActiveItemWrapper
                  isActive={activeItem === 'thirdImage-image'}
                  isPreview={isPreview}
                >
                  <AspectRatio ratio={7 / 5}>
                    <Image
                      fill
                      src={images?.[2].src}
                      alt=""
                      objectFit="cover"
                      pointerEvents={isPreview ? 'none' : undefined}
                    />
                  </AspectRatio>
                </ActiveItemWrapper>
                {images?.[2].caption && (
                  <Text
                    mt="space.16"
                    bg={activeItem === 'thirdImage-caption' ? 'arcticBlue' : 'unset'}
                    transition="all 120ms ease-in-out"
                  >
                    {images?.[2].caption}
                  </Text>
                )}
              </Flex>
            </MotionBox>
          )}
        </Box>
      </Flex>
      <Flex
        bg="black"
        color="white"
        flexDir="column"
        display={{ base: 'flex', md: 'none' }}
        gap="space.32"
        py="space.32"
      >
        {images?.[1] && (
          <Box ml="auto" w="100%" maxW={rem(128)}>
            <AspectRatio w="100%" ratio={7 / 10}>
              <Image
                fill
                src={images?.[1].src}
                alt=""
                objectFit="cover"
                pointerEvents={isPreview ? 'none' : undefined}
              />
            </AspectRatio>
            {images?.[1].caption && (
              <Text fontSize="2xs" w="100%" mt="space.8" textAlign="right">
                {images?.[1].caption}
              </Text>
            )}
          </Box>
        )}
        {images?.[0] && (
          <Box pr="space.96">
            <AspectRatio w="100%" ratio={9 / 12}>
              <Image
                fill
                src={images?.[0].src}
                alt=""
                objectFit="cover"
                pointerEvents={isPreview ? 'none' : undefined}
              />
            </AspectRatio>
            {images?.[0].caption && (
              <Text fontSize="2xs" mt="space.8">
                {images?.[0].caption}
              </Text>
            )}
          </Box>
        )}
        {images?.[2] && (
          <Box px="space.32">
            <AspectRatio w="100%" ratio={7 / 5}>
              <Image
                fill
                src={images?.[2].src}
                alt=""
                objectFit="cover"
                pointerEvents={isPreview ? 'none' : undefined}
              />
            </AspectRatio>
            {images?.[2].caption && (
              <Text fontSize="2xs" mt="space.8">
                {images?.[2].caption}
              </Text>
            )}
          </Box>
        )}
        {title && (
          <Heading fontSize="xl" mt="space.32" mr="space.64" letterSpacing="subtitle">
            {title}
          </Heading>
        )}
      </Flex>
    </Container>
  );
};
